import { useRef } from 'react';
import { MicOff, Pause, Play, Square } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components';
import { usePublishBottomNavbarHeight } from 'components/navbars/BottomNavbar';
import { formatTime } from 'utils/audioUpload';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { RecordingPhase } from 'hooks/useRecording';

interface WithoutRecordingBottomNavbarProps {
  recordingPhase: RecordingPhase;
  elapsedTime: number;
  onTogglePause: () => void;
  onStop: () => void;
  canStop: boolean;
}

export const WithoutRecordingBottomNavbar = (props: WithoutRecordingBottomNavbarProps) =>
  props.recordingPhase === RecordingPhase.IDLE ? null : <RunningBar {...props} />;

const RunningBar = ({
  recordingPhase,
  elapsedTime,
  onTogglePause,
  onStop,
  canStop,
}: WithoutRecordingBottomNavbarProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const barRef = useRef<HTMLDivElement>(null);
  usePublishBottomNavbarHeight(barRef);

  const isPaused = recordingPhase === RecordingPhase.PAUSED;
  const ToggleIcon = isPaused ? Play : Pause;

  return (
    <div
      ref={barRef}
      className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-2 border-t border-border bg-card p-3"
      data-recording-bottom-bar
    >
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <MicOff className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <p role="status" className="truncate text-xs font-semibold text-muted-foreground">
          {t('recording.withoutRecording.running')}
        </p>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="secondary"
          data-ph-no-autocapture
          onClick={() => {
            trackEvent(
              isPaused
                ? ANALYTICS_EVENTS.PATIENT_RESUME_RECORDING
                : ANALYTICS_EVENTS.PATIENT_PAUSE_RECORDING
            );
            onTogglePause();
          }}
        >
          <ToggleIcon className="size-4" aria-hidden />
          {t(isPaused ? 'recording.withoutRecording.resume' : 'recording.withoutRecording.pause')}
        </Button>
        <Button
          data-ph-no-autocapture
          disabled={!canStop}
          onClick={() => {
            trackEvent(ANALYTICS_EVENTS.PATIENT_STOP_RECORDING);
            onStop();
          }}
        >
          <Square className="size-4" aria-hidden />
          {t('recording.withoutRecording.stop')}
          <span
            role="timer"
            className="flex h-5 min-w-5 items-center justify-center rounded-sm bg-background/20 px-1 text-xs font-medium text-background"
          >
            {formatTime(elapsedTime)}
          </span>
        </Button>
      </div>
    </div>
  );
};
